import {Component, EventEmitter, HostListener, Input, OnChanges, Output} from '@angular/core';
import SouthKoreaMap from '@svg-maps/south-korea';
import KoreaSigunguMap from '../../../../../../assets/map/korea-sigungu.json';
import moment from 'moment';
import {getCanonicalHouseRegion, getHouseAddressTokens} from './house-region.util';

interface RegionMapConfig {
  region: string;
  label: string;
  labelX: number;
  labelY: number;
  labelAnchor?: 'start' | 'middle' | 'end';
}

interface RegionPurposeCount {
  purpose: string;
  count: number;
}

interface RegionMapArea {
  id: string;
  path: string;
  region: string;
  label: string;
  count: number;
  level: number;
  labelX: number;
  labelY: number;
  labelAnchor: 'start' | 'middle' | 'end';
  compactLabel: boolean;
  hasWarnPeriodBadge: boolean;
  purposes: RegionPurposeCount[];
}

interface SigunguFeature {
  geometry: {
    type: 'Polygon' | 'MultiPolygon';
    coordinates: unknown;
  };
  properties: {
    sido: string;
    sidonm: string;
    sgg: string;
    sggnm: string;
  };
}

interface RegistrationMapMarker {
  key: string;
  region: string;
  label: string;
  count: number;
  x: number;
  y: number;
  hasWarnPeriodBadge: boolean;
}

interface SummaryMapMarker {
  key: string;
  x: number;
  y: number;
  tone: 'accent' | 'warn' | 'analysis';
}

@Component({
  selector: 'app-house-region-map',
  templateUrl: './house-region-map.component.html',
  styleUrls: ['./house-region-map.component.scss']
})
export class HouseRegionMapComponent implements OnChanges {
  @Input() items: any[] = [];
  @Input() analysisItems: any[] = [];
  @Input() compact = false;
  @Input() smallLabels = false;
  @Input() largeLabels = false;
  @Input() mode: 'changes' | 'registrations' | 'summary' = 'changes';
  @Input() selectedRegion?: string;
  @Output() readonly regionSelected = new EventEmitter<string>();

  readonly regionMapViewBox = SouthKoreaMap.viewBox;
  // 사이드 카드에서는 원본 SVG의 바깥 여백을 줄여 지도를 더 크게 보여준다.
  readonly compactRegionMapViewBox = '45 8 434 615';
  regionMapAreas: RegionMapArea[] = [];
  registrationMapMarkers: RegistrationMapMarker[] = [];
  summaryMapMarkers: SummaryMapMarker[] = [];
  selectedRegionDetails?: RegionMapArea;

  private readonly regionMapConfig: Record<string, RegionMapConfig> = {
    'seoul': {region: '서울특별시', label: '서울', labelX: 132, labelY: 146, labelAnchor: 'end'},
    'busan': {region: '부산광역시', label: '부산', labelX: 355, labelY: 413},
    'daegu': {region: '대구광역시', label: '대구', labelX: 300, labelY: 344},
    'incheon': {region: '인천광역시', label: '인천', labelX: 107, labelY: 167, labelAnchor: 'end'},
    'gwangju': {region: '광주광역시', label: '광주', labelX: 138, labelY: 408},
    'daejeon': {region: '대전광역시', label: '대전', labelX: 178, labelY: 280, labelAnchor: 'end'},
    'ulsan': {region: '울산광역시', label: '울산', labelX: 382, labelY: 366, labelAnchor: 'start'},
    'sejong': {region: '세종특별자치시', label: '세종', labelX: 154, labelY: 249, labelAnchor: 'end'},
    'gyeonggi': {region: '경기도', label: '경기', labelX: 180, labelY: 154},
    'gangwon': {region: '강원특별자치도', label: '강원', labelX: 326, labelY: 112},
    'north-chungcheong': {region: '충청북도', label: '충북', labelX: 245, labelY: 247},
    'south-chungcheong': {region: '충청남도', label: '충남', labelX: 139, labelY: 298},
    'north-jeolla': {region: '전북특별자치도', label: '전북', labelX: 180, labelY: 365},
    'south-jeolla': {region: '전라남도', label: '전남', labelX: 139, labelY: 472},
    'north-gyeongsang': {region: '경상북도', label: '경북', labelX: 328, labelY: 277},
    'south-gyeongsang': {region: '경상남도', label: '경남', labelX: 277, labelY: 421},
    'jeju': {region: '제주특별자치도', label: '제주', labelX: 111, labelY: 612}
  };
  // 동시에 표시될 때 라벨이 겹칠 가능성이 높은 인접 지역을 묶어 관리한다.
  private readonly regionLabelOverlapGroups = [
    ['seoul', 'incheon', 'gyeonggi'],
    ['sejong', 'daejeon', 'north-chungcheong'],
    ['daegu', 'north-gyeongsang'],
    ['busan', 'ulsan', 'south-gyeongsang'],
    ['gwangju', 'south-jeolla']
  ];

  ngOnChanges(): void {
    this.createRegionMapData(this.items ?? []);
  }

  openRegionDetails(area: RegionMapArea): void {
    if (area.count === 0) {
      return;
    }

    // 등록 현황 지도는 팝업 대신 부모 목록에 선택 지역을 전달한다.
    if (this.mode === 'registrations') {
      this.regionSelected.emit(area.region);
    } else {
      this.selectedRegionDetails = area;
    }
  }

  selectRegistrationMarker(marker: RegistrationMapMarker): void {
    this.regionSelected.emit(marker.region);
  }

  getAreaClass(area: RegionMapArea): string {
    if (this.mode === 'summary') {
      const activeClass = area.count > 0 ? ' house-region-map__area--summary-active' : '';
      return `house-region-map__area house-region-map__area--level-0${activeClass}`;
    }

    // 통계 지도는 warn, 리스트 등록 지도는 accent 색상 단계를 사용한다.
    const levelClass = this.mode === 'registrations'
      ? `house-region-map__area--accent-level-${area.level}`
      : `house-region-map__area--level-${area.level}`;
    const interactiveClass = area.count > 0 ? ' house-region-map__area--interactive' : '';
    const selectedClass = area.region === this.selectedRegion ? ' house-region-map__area--selected' : '';

    return `house-region-map__area ${levelClass}${interactiveClass}${selectedClass}`;
  }

  getAreaAriaLabel(area: RegionMapArea): string {
    if (this.mode === 'summary') {
      return area.label;
    }

    if (this.mode === 'registrations') {
      return area.count > 0
        ? `${area.label} 등록 부동산 ${area.count}건, 지역 목록 보기`
        : `${area.label} 등록 부동산 없음`;
    }

    return area.count > 0
      ? `${area.label} 변경등기 ${area.count}건, 상세보기`
      : `${area.label} 변경등기 없음`;
  }

  closeRegionDetails(): void {
    this.selectedRegionDetails = undefined;
  }

  @HostListener('document:keydown.escape')
  closeRegionDetailsOnEscape(): void {
    this.closeRegionDetails();
  }

  private createRegionMapData(items: any[]): void {
    const regions = this.groupItemsByRegion(items);
    const warnPeriodRegions = this.mode === 'registrations'
      ? this.getWarnPeriodRegions(items)
      : new Set<string>();
    const purposesByRegion = this.mode === 'changes'
      ? this.groupChangedRegistrationPurposesByRegion(items)
      : new Map<string, Map<string, number>>();
    const maximumCount = Math.max(...Array.from(regions.values()), 0);
    // 실제 값이 표시되는 지역만 대상으로 라벨 중복 여부를 계산한다.
    const activeLocationIds = new Set(SouthKoreaMap.locations
      .filter((location: any) => {
        const region = this.regionMapConfig[location.id]?.region;
        return region && (regions.get(region) ?? 0) > 0;
      })
      .map((location: any) => location.id));

    this.selectedRegionDetails = undefined;
    this.registrationMapMarkers = this.mode === 'registrations'
      ? this.createRegistrationMapMarkers(items)
      : [];
    this.summaryMapMarkers = this.mode === 'summary'
      ? this.createSummaryMapMarkers(items, this.analysisItems ?? [])
      : [];
    this.regionMapAreas = SouthKoreaMap.locations.map((location: any) => {
      const config = this.regionMapConfig[location.id];
      const count = config ? regions.get(config.region) ?? 0 : 0;

      return {
        id: location.id,
        path: location.path,
        region: config?.region ?? location.name,
        label: config?.label ?? location.name,
        count,
        level: this.getColorLevel(count, maximumCount),
        labelX: config?.labelX ?? 0,
        labelY: config?.labelY ?? 0,
        labelAnchor: config?.labelAnchor ?? 'middle',
        compactLabel: this.hasOverlappingLabel(location.id, activeLocationIds),
        hasWarnPeriodBadge: warnPeriodRegions.has(config?.region ?? ''),
        purposes: Array.from(purposesByRegion.get(config?.region ?? '')?.entries() ?? [])
          .map(([purpose, purposeCount]) => ({purpose, count: purposeCount}))
          .sort((first, second) => second.count - first.count || first.purpose.localeCompare(second.purpose, 'ko'))
      };
    });
  }

  private hasOverlappingLabel(locationId: string, activeLocationIds: Set<any>): boolean {
    // 같은 밀집 그룹에서 두 지역 이상이 활성화된 경우에만 작은 라벨을 적용한다.
    return this.regionLabelOverlapGroups.some(group => {
      return group.includes(locationId) && group.filter(id => activeLocationIds.has(id)).length > 1;
    });
  }

  private getColorLevel(count: number, maximumCount: number): number {
    if (count === 0 || maximumCount === 0) {
      return 0;
    }

    if (this.mode === 'registrations') {
      // 등록 수 1건부터 1, 2~3, 4~7, 8건 이상의 네 단계로 진해진다.
      return Math.min(4, Math.ceil(Math.log2(count + 1)));
    }

    return Math.max(1, Math.ceil((count / maximumCount) * 4));
  }

  private groupItemsByRegion(items: any[]): Map<string, number> {
    const regions = new Map<string, number>();

    items.forEach(item => {
      const region = getCanonicalHouseRegion(item.address);
      regions.set(region, (regions.get(region) ?? 0) + 1);
    });

    return regions;
  }

  private getWarnPeriodRegions(items: any[]): Set<string> {
    const today = moment().startOf('day');

    return new Set(items
      .filter(item => {
        if (!item.changedAt) {
          return false;
        }

        const changedDate = moment(item.changedAt).startOf('day');
        const elapsedDays = today.diff(changedDate, 'days');

        return changedDate.isValid() && elapsedDays >= 0 && elapsedDays <= 7;
      })
      .map(item => getCanonicalHouseRegion(item.address)));
  }

  private createRegistrationMapMarkers(items: any[]): RegistrationMapMarker[] {
    const markers = new Map<string, RegistrationMapMarker>();

    items.forEach(item => {
      const region = getCanonicalHouseRegion(item.address);
      const district = this.getProvincialDistrict(item.address, region);
      const position = district ? this.getDistrictPosition(region, district) : undefined;
      const config = Object.values(this.regionMapConfig).find(candidate => candidate.region === region);

      // 시·군 위치를 찾지 못한 주소는 기존 시·도 라벨 위치에 안전하게 표시한다.
      if (!position && !config) {
        return;
      }

      const key = district && position ? `${region}:${district}` : region;
      const current = markers.get(key);
      const hasWarnPeriodBadge = this.isWarnPeriodItem(item);

      if (current) {
        current.count += 1;
        current.hasWarnPeriodBadge ||= hasWarnPeriodBadge;
        return;
      }

      markers.set(key, {
        key,
        region,
        label: district && position ? district.replace(/(시|군)$/, '') : config!.label,
        count: 1,
        x: position?.[0] ?? config!.labelX,
        y: position?.[1] ?? config!.labelY,
        hasWarnPeriodBadge
      });
    });

    return Array.from(markers.values());
  }

  private createSummaryMapMarkers(houses: any[], analyses: any[]): SummaryMapMarker[] {
    const markerGroups = new Map<string, {positionKey: string; x: number; y: number; tone: SummaryMapMarker['tone']}>();

    const appendMarker = (item: any, type: 'house' | 'analysis') => {
      const region = getCanonicalHouseRegion(item.address);
      const district = this.getProvincialDistrict(item.address, region);
      const position = district ? this.getDistrictPosition(region, district) : undefined;
      const config = Object.values(this.regionMapConfig).find(candidate => candidate.region === region);

      if (!position && !config) {
        return;
      }

      const positionKey = district && position ? `${region}:${district}` : region;
      const tone: SummaryMapMarker['tone'] = type === 'analysis'
        ? 'analysis'
        : (this.isWarnPeriodItem(item) ? 'warn' : 'accent');
      const groupKey = `${positionKey}:${tone}`;
      const baseX = position?.[0] ?? config!.labelX;
      const baseY = position?.[1] ?? config!.labelY;

      // 작은 지도에서는 같은 시·군·상태의 부동산을 하나의 원으로 합쳐 과밀 렌더링을 방지한다.
      markerGroups.set(groupKey, {positionKey, x: baseX, y: baseY, tone});
    };

    houses.forEach(item => appendMarker(item, 'house'));
    analyses.forEach(item => appendMarker(item, 'analysis'));

    const overlapCounts = new Map<string, number>();
    const markers = Array.from(markerGroups.entries()).map(([key, marker]) => {
      const overlapIndex = overlapCounts.get(marker.positionKey) ?? 0;
      overlapCounts.set(marker.positionKey, overlapIndex + 1);
      const angle = overlapIndex * 2.399963;
      const radius = overlapIndex === 0 ? 0 : 14;

      return {
        key,
        x: marker.x + Math.cos(angle) * radius,
        y: marker.y + Math.sin(angle) * radius,
        tone: marker.tone
      };
    });

    return markers;
  }

  private getProvincialDistrict(address: unknown, region: string): string | undefined {
    // 광역시와 세종시는 구 단위로 나누지 않고 하나의 지역 마커로 집계한다.
    if (region.endsWith('광역시') || region.endsWith('특별시') || region === '세종특별자치시') {
      return undefined;
    }

    const tokens = getHouseAddressTokens(address);
    return tokens.slice(1, 4).find(token => /(?:시|군)$/.test(token));
  }

  private getDistrictPosition(region: string, district: string): [number, number] | undefined {
    const feature = (KoreaSigunguMap.features as SigunguFeature[]).find(candidate => {
      return candidate.properties.sggnm === district && this.getFeatureRegion(candidate) === region;
    });

    if (!feature) {
      return undefined;
    }

    const ring = this.getLargestExteriorRing(feature);
    return ring ? this.projectCoordinate(this.getRingCentroid(ring)) : undefined;
  }

  private getFeatureRegion(feature: SigunguFeature): string {
    // 기존 통합 코드 12에서 122·123은 광주, 나머지는 전남 시·군으로 구분된다.
    if (feature.properties.sido === '12') {
      return /^12[23]/.test(feature.properties.sgg) ? '광주광역시' : '전라남도';
    }

    return getCanonicalHouseRegion(feature.properties.sidonm);
  }

  private getLargestExteriorRing(feature: SigunguFeature): Array<[number, number]> | undefined {
    const polygons = feature.geometry.type === 'Polygon'
      ? [feature.geometry.coordinates as Array<Array<[number, number]>>]
      : feature.geometry.coordinates as Array<Array<Array<[number, number]>>>;

    return polygons
      .map(polygon => polygon[0])
      .filter(ring => ring?.length)
      .sort((first, second) => Math.abs(this.getRingArea(second)) - Math.abs(this.getRingArea(first)))[0];
  }

  private getRingArea(ring: Array<[number, number]>): number {
    return ring.reduce((area, point, index) => {
      const next = ring[(index + 1) % ring.length];
      return area + point[0] * next[1] - next[0] * point[1];
    }, 0) / 2;
  }

  private getRingCentroid(ring: Array<[number, number]>): [number, number] {
    const area = this.getRingArea(ring);
    if (Math.abs(area) < Number.EPSILON) {
      return ring[0];
    }

    let longitude = 0;
    let latitude = 0;
    ring.forEach((point, index) => {
      const next = ring[(index + 1) % ring.length];
      const factor = point[0] * next[1] - next[0] * point[1];
      longitude += (point[0] + next[0]) * factor;
      latitude += (point[1] + next[1]) * factor;
    });

    return [longitude / (6 * area), latitude / (6 * area)];
  }

  private projectCoordinate([longitude, latitude]: [number, number]): [number, number] {
    // korea-sigungu GeoJSON의 경위도를 전국 SVG(524×631) 좌표계로 투영한다.
    const longitudeScale = Math.cos(36.16716937279025 * Math.PI / 180);
    const minX = 100.59731686170083;
    const maxLatitude = 38.617;
    const scale = 102.90253149026083;
    const offsetY = 32.28650770592964;

    return [
      (longitude * longitudeScale - minX) * scale,
      offsetY + (maxLatitude - latitude) * scale
    ];
  }

  private isWarnPeriodItem(item: any): boolean {
    if (!item.changedAt) {
      return false;
    }

    const changedDate = moment(item.changedAt).startOf('day');
    const elapsedDays = moment().startOf('day').diff(changedDate, 'days');
    return changedDate.isValid() && elapsedDays >= 0 && elapsedDays <= 7;
  }

  private groupChangedRegistrationPurposesByRegion(changeItems: any[]): Map<string, Map<string, number>> {
    const regions = new Map<string, Map<string, number>>();

    changeItems.forEach(change => {
      const region = getCanonicalHouseRegion(change.address);
      const purpose = String(change.purpose ?? '').trim() || '기타';
      const purposes = regions.get(region) ?? new Map<string, number>();
      purposes.set(purpose, (purposes.get(purpose) ?? 0) + 1);
      regions.set(region, purposes);
    });

    return regions;
  }
}
